import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import UserAvatar from "@/components/UserAvatar";
import SubSettingsShell from "@/components/SubSettingsShell";

/**
 * BlockedUsers — iOS `settings/blocked.tsx`.
 *
 * List of blocked accounts with a per-row "Unblock" button.  Uses the
 * shared iOS-style sub-settings shell.
 */
export default function BlockedUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(EP.myBlocked(1, 50));
        const d = unwrap(res.data);
        const list = Array.isArray(d)
          ? d
          : d?.items || d?.users || d?.blocked || d?.data || [];
        setItems(list || []);
      } catch (e) {
        setErr(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const unblock = async (id, username) => {
    try {
      // iOS spec: POST /unblock/{user_id} REQUIRES { username } body.
      await api.post(EP.unblockUser(id), { username });
      setItems((prev) => prev.filter((u) => (u.id ?? u.user_id) !== id));
      toast.success("Unblocked.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <SubSettingsShell
      title="Blocked accounts"
      subtitle="These accounts cannot follow you, see your posts or send you feedback."
      testId="blocked-screen"
    >
      {loading ? (
        <div className="flex justify-center py-10" data-testid="blocked-loading">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#7638FA" }} />
        </div>
      ) : err ? (
        <div
          className="rounded-2xl p-4"
          style={{ backgroundColor: "rgba(255, 59, 48, 0.13)" }}
          data-testid="blocked-error"
        >
          <p className="text-[#FF3B30] text-sm">{err}</p>
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-[20px] border border-border bg-card py-12 flex flex-col items-center text-center"
          data-testid="blocked-empty"
        >
          <ShieldOff size={22} className="text-muted-foreground mb-2.5" strokeWidth={1.7} />
          <p className="text-muted-foreground text-sm">You haven&apos;t blocked anyone.</p>
        </div>
      ) : (
        <div className="rounded-[20px] border border-border bg-card overflow-hidden">
          {items.map((u, i) => {
            const id = u.id ?? u.user_id;
            const isLast = i === items.length - 1;
            return (
              <div
                key={id}
                className={`flex items-center gap-3 px-4 py-3 ${
                  isLast ? "" : "border-b border-border"
                }`}
                data-testid={`blocked-item-${id}`}
              >
                <Link
                  to={`/${u.username}`}
                  className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80"
                >
                  <UserAvatar user={u} size="md" />
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground text-[14.5px] font-semibold truncate">
                      {u.display_name || u.username}
                    </div>
                    <div className="text-muted-foreground text-[12.5px] truncate">
                      @{u.username}
                    </div>
                  </div>
                </Link>
                <button
                  type="button"
                  onClick={() => unblock(id, u.username)}
                  className="h-9 rounded-full px-4 text-[13px] font-bold bg-secondary text-foreground hover:opacity-90 transition-opacity"
                  data-testid={`blocked-unblock-${id}`}
                >
                  Unblock
                </button>
              </div>
            );
          })}
        </div>
      )}
    </SubSettingsShell>
  );
}
